/**
 * Sound System - THE ONESCENT DIAL
 * Cosmic Audio Experience with Theme-Aware Soundscapes
 * 
 * Uses Web Audio API for synthesized sounds (no external dependencies)
 * Sounds are themed to complement different panels and content.
 */

// =============================================================================
// AUDIO CONTEXT SINGLETON
// =============================================================================

let audioContext = null;
let masterGain = null;
let ambientGain = null;
let effectsGain = null;
let currentAmbient = null;

const getAudioContext = () => {
  if (!audioContext) {
    audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    // Master volume control
    masterGain = audioContext.createGain();
    masterGain.connect(audioContext.destination);
    masterGain.gain.value = 0.5;
    
    // Separate gains for ambient and effects
    ambientGain = audioContext.createGain();
    ambientGain.connect(masterGain);
    ambientGain.gain.value = 0.5; // Increased from 0.3 for better audibility
    
    effectsGain = audioContext.createGain();
    effectsGain.connect(masterGain);
    effectsGain.gain.value = 0.5;
  }
  return audioContext;
};

// Resume audio context (required after user interaction)
export const resumeAudio = async () => {
  const ctx = getAudioContext();
  if (ctx.state === 'suspended') {
    await ctx.resume();
  }
  return ctx;
};

// =============================================================================
// VOLUME SETTINGS
// =============================================================================

export const VOLUME_LEVELS = {
  off: 0,
  low: 0.25,
  medium: 0.5,
  high: 0.75,
};

export const setMasterVolume = (level) => {
  if (!masterGain) getAudioContext();
  const volume = VOLUME_LEVELS[level] ?? level;
  masterGain.gain.setTargetAtTime(volume, audioContext.currentTime, 0.1);
};

export const setAmbientVolume = (level) => {
  if (!ambientGain) getAudioContext();
  const volume = VOLUME_LEVELS[level] ?? level;
  // Direct volume without extra scaling for better audibility
  ambientGain.gain.setTargetAtTime(volume, audioContext.currentTime, 0.1);
};

export const setEffectsVolume = (level) => {
  if (!effectsGain) getAudioContext();
  const volume = VOLUME_LEVELS[level] ?? level;
  effectsGain.gain.setTargetAtTime(volume, audioContext.currentTime, 0.1);
};

// =============================================================================
// SYNTHESIZED SOUND GENERATORS
// =============================================================================

// Create a soft chime sound
const createChime = (frequency = 800, duration = 0.3, type = 'sine') => {
  const ctx = getAudioContext();
  const oscillator = ctx.createOscillator();
  const gainNode = ctx.createGain();
  
  oscillator.type = type;
  oscillator.frequency.setValueAtTime(frequency, ctx.currentTime);
  oscillator.frequency.exponentialRampToValueAtTime(frequency * 1.5, ctx.currentTime + duration * 0.1);
  oscillator.frequency.exponentialRampToValueAtTime(frequency * 0.8, ctx.currentTime + duration);
  
  gainNode.gain.setValueAtTime(0, ctx.currentTime);
  gainNode.gain.linearRampToValueAtTime(0.3, ctx.currentTime + 0.02);
  gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
  
  oscillator.connect(gainNode);
  gainNode.connect(effectsGain);
  
  oscillator.start(ctx.currentTime);
  oscillator.stop(ctx.currentTime + duration);
};

// Create crystalline tone (for celestial/planetary themes)
const createCrystalline = (baseFreq = 1200, duration = 0.5) => {
  const ctx = getAudioContext();
  const frequencies = [baseFreq, baseFreq * 1.5, baseFreq * 2];
  
  frequencies.forEach((freq, i) => {
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(freq, ctx.currentTime);
    
    const delay = i * 0.05;
    gainNode.gain.setValueAtTime(0, ctx.currentTime + delay);
    gainNode.gain.linearRampToValueAtTime(0.15 / (i + 1), ctx.currentTime + delay + 0.03);
    gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
    
    oscillator.connect(gainNode);
    gainNode.connect(effectsGain);
    
    oscillator.start(ctx.currentTime + delay);
    oscillator.stop(ctx.currentTime + duration);
  });
};

// Create whoosh sound (for modals/panels)
const createWhoosh = (direction = 'in', duration = 0.25) => {
  const ctx = getAudioContext();
  
  // Noise generator for whoosh
  const bufferSize = ctx.sampleRate * duration;
  const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  
  for (let i = 0; i < bufferSize; i++) {
    data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / bufferSize, 2);
  }
  
  const noise = ctx.createBufferSource();
  noise.buffer = buffer;
  
  // Filter for smoothness
  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.setValueAtTime(direction === 'in' ? 500 : 2000, ctx.currentTime);
  filter.frequency.exponentialRampToValueAtTime(
    direction === 'in' ? 2000 : 500, 
    ctx.currentTime + duration
  );
  
  const gainNode = ctx.createGain();
  gainNode.gain.setValueAtTime(direction === 'in' ? 0 : 0.15, ctx.currentTime);
  gainNode.gain.linearRampToValueAtTime(direction === 'in' ? 0.15 : 0.001, ctx.currentTime + duration * 0.8);
  gainNode.gain.linearRampToValueAtTime(0.001, ctx.currentTime + duration);
  
  noise.connect(filter);
  filter.connect(gainNode);
  gainNode.connect(effectsGain);
  
  noise.start(ctx.currentTime);
};

// Create success sound (harmonic rise)
const createSuccess = () => {
  const ctx = getAudioContext();
  const notes = [523.25, 659.25, 783.99]; // C5, E5, G5 major chord arpeggio
  
  notes.forEach((freq, i) => {
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(freq, ctx.currentTime);
    
    const delay = i * 0.08;
    gainNode.gain.setValueAtTime(0, ctx.currentTime + delay);
    gainNode.gain.linearRampToValueAtTime(0.2, ctx.currentTime + delay + 0.02);
    gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + delay + 0.4);
    
    oscillator.connect(gainNode);
    gainNode.connect(effectsGain);
    
    oscillator.start(ctx.currentTime + delay);
    oscillator.stop(ctx.currentTime + delay + 0.5);
  });
};

// Create error/warning sound
const createError = () => {
  const ctx = getAudioContext();
  const oscillator = ctx.createOscillator();
  const gainNode = ctx.createGain();
  
  oscillator.type = 'triangle';
  oscillator.frequency.setValueAtTime(220, ctx.currentTime);
  oscillator.frequency.setValueAtTime(180, ctx.currentTime + 0.1);
  
  gainNode.gain.setValueAtTime(0, ctx.currentTime);
  gainNode.gain.linearRampToValueAtTime(0.25, ctx.currentTime + 0.02);
  gainNode.gain.setValueAtTime(0.25, ctx.currentTime + 0.1);
  gainNode.gain.linearRampToValueAtTime(0.2, ctx.currentTime + 0.12);
  gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
  
  oscillator.connect(gainNode);
  gainNode.connect(effectsGain);
  
  oscillator.start(ctx.currentTime);
  oscillator.stop(ctx.currentTime + 0.4);
};

// Create notification ping
const createNotification = () => {
  const ctx = getAudioContext();
  const frequencies = [880, 1108.73]; // A5, C#6
  
  frequencies.forEach((freq, i) => {
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(freq, ctx.currentTime);
    
    const delay = i * 0.12;
    gainNode.gain.setValueAtTime(0, ctx.currentTime + delay);
    gainNode.gain.linearRampToValueAtTime(0.2, ctx.currentTime + delay + 0.01);
    gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + delay + 0.3);
    
    oscillator.connect(gainNode);
    gainNode.connect(effectsGain);
    
    oscillator.start(ctx.currentTime + delay);
    oscillator.stop(ctx.currentTime + delay + 0.35);
  });
};

// Create soft click
const createClick = () => {
  const ctx = getAudioContext();
  const oscillator = ctx.createOscillator();
  const gainNode = ctx.createGain();
  
  oscillator.type = 'sine';
  oscillator.frequency.setValueAtTime(600, ctx.currentTime);
  oscillator.frequency.exponentialRampToValueAtTime(200, ctx.currentTime + 0.05);
  
  gainNode.gain.setValueAtTime(0.15, ctx.currentTime);
  gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.05);
  
  oscillator.connect(gainNode);
  gainNode.connect(effectsGain);
  
  oscillator.start(ctx.currentTime);
  oscillator.stop(ctx.currentTime + 0.06);
};

// =============================================================================
// AMBIENT SOUNDSCAPES (Theme-Based)
// =============================================================================

// Cosmic drone/hum (for Spacio/Location)
const createCosmicDrone = () => {
  const ctx = getAudioContext();
  
  // Base frequencies for cosmic hum
  const frequencies = [55, 82.5, 110]; // A1, E2, A2
  const oscillators = [];
  const gains = [];
  
  frequencies.forEach((freq, i) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, ctx.currentTime);
    
    // Add subtle frequency modulation
    const lfo = ctx.createOscillator();
    const lfoGain = ctx.createGain();
    lfo.frequency.setValueAtTime(0.1 + i * 0.05, ctx.currentTime);
    lfoGain.gain.setValueAtTime(freq * 0.02, ctx.currentTime);
    lfo.connect(lfoGain);
    lfoGain.connect(osc.frequency);
    lfo.start();
    
    // Increased gain for better audibility (was 0.08)
    gain.gain.setValueAtTime(0.25 / (i + 1), ctx.currentTime);
    
    osc.connect(gain);
    gain.connect(ambientGain);
    osc.start();
    
    oscillators.push(osc);
    gains.push(gain);
  });
  
  return {
    stop: () => {
      gains.forEach(g => g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 1));
      setTimeout(() => oscillators.forEach(o => o.stop()), 1100);
    }
  };
};

// Solar wind sound (for Space Weather)
const createSolarWind = () => {
  const ctx = getAudioContext();
  
  // Filtered noise for wind effect
  const bufferSize = ctx.sampleRate * 4;
  const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  
  for (let i = 0; i < bufferSize; i++) {
    data[i] = Math.random() * 2 - 1;
  }
  
  const noise = ctx.createBufferSource();
  noise.buffer = buffer;
  noise.loop = true;
  
  const filter = ctx.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.setValueAtTime(400, ctx.currentTime);
  filter.Q.setValueAtTime(0.5, ctx.currentTime);
  
  // Modulate filter for movement
  const lfo = ctx.createOscillator();
  const lfoGain = ctx.createGain();
  lfo.frequency.setValueAtTime(0.15, ctx.currentTime);
  lfoGain.gain.setValueAtTime(200, ctx.currentTime);
  lfo.connect(lfoGain);
  lfoGain.connect(filter.frequency);
  lfo.start();
  
  const gainNode = ctx.createGain();
  gainNode.gain.setValueAtTime(0.06, ctx.currentTime);
  
  noise.connect(filter);
  filter.connect(gainNode);
  gainNode.connect(ambientGain);
  noise.start();
  
  return {
    stop: () => {
      gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 1);
      setTimeout(() => noise.stop(), 1100);
    }
  };
};

// Crystalline ambience (for Celestial/Planetary)
const createCrystallineAmbient = () => {
  const ctx = getAudioContext();
  const oscillators = [];
  const gains = [];
  
  // Create ethereal pad
  const frequencies = [261.63, 329.63, 392, 493.88]; // C4, E4, G4, B4
  
  frequencies.forEach((freq, i) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq * 2, ctx.currentTime); // Octave up for sparkle
    
    // Tremolo effect
    const lfo = ctx.createOscillator();
    const lfoGain = ctx.createGain();
    lfo.frequency.setValueAtTime(0.5 + i * 0.2, ctx.currentTime);
    lfoGain.gain.setValueAtTime(0.03, ctx.currentTime);
    lfo.connect(lfoGain);
    lfoGain.connect(gain.gain);
    lfo.start();
    
    gain.gain.setValueAtTime(0.04 / (i + 1), ctx.currentTime);
    
    osc.connect(gain);
    gain.connect(ambientGain);
    osc.start();
    
    oscillators.push(osc);
    gains.push(gain);
  });
  
  return {
    stop: () => {
      gains.forEach(g => g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 1.5));
      setTimeout(() => oscillators.forEach(o => o.stop()), 1600);
    }
  };
};

// =============================================================================
// SOUND EFFECTS API
// =============================================================================

export const SoundEffects = {
  // UI Interactions
  click: () => createClick(),
  
  // Modal/Panel
  modalOpen: () => createWhoosh('in'),
  modalClose: () => createWhoosh('out'),
  
  // Feedback
  success: () => createSuccess(),
  error: () => createError(),
  notification: () => createNotification(),
  
  // Themed sounds
  chime: (freq) => createChime(freq),
  crystalline: (freq) => createCrystalline(freq),
  
  // Play themed sound based on content type
  themed: (theme) => {
    switch (theme) {
      case 'spacio':
      case 'location':
        createChime(400, 0.4, 'triangle');
        break;
      case 'temporal':
      case 'time':
        createChime(600, 0.3, 'sine');
        break;
      case 'celestial':
      case 'planetary':
        createCrystalline(1000, 0.5);
        break;
      case 'solar':
      case 'weather':
        createChime(800, 0.35, 'sine');
        break;
      case 'synthesis':
        createCrystalline(1200, 0.6);
        break;
      default:
        createChime(700, 0.3, 'sine');
    }
  }
};

// =============================================================================
// AMBIENT SOUNDSCAPES API
// =============================================================================

export const AmbientSounds = {
  // Start ambient for specific theme
  start: (theme) => {
    // Stop any existing ambient
    if (currentAmbient) {
      currentAmbient.stop();
      currentAmbient = null;
    }
    
    resumeAudio();
    
    switch (theme) {
      case 'spacio':
      case 'location':
        currentAmbient = createCosmicDrone();
        break;
      case 'solar':
      case 'weather':
        currentAmbient = createSolarWind();
        break;
      case 'celestial':
      case 'planetary':
      case 'synthesis':
        currentAmbient = createCrystallineAmbient();
        break;
      default:
        currentAmbient = createCosmicDrone();
    }
    
    return currentAmbient;
  },
  
  // Stop current ambient
  stop: () => {
    if (currentAmbient) {
      currentAmbient.stop();
      currentAmbient = null;
    }
  },
  
  // Check if ambient is playing
  isPlaying: () => currentAmbient !== null,
};

// =============================================================================
// SOUND PREFERENCES
// =============================================================================

const SOUND_PREFS_KEY = 'tod_sound_preferences';

export const getDefaultSoundPrefs = () => ({
  enabled: true, // On by default - cosmic drone auto-plays after first interaction
  masterVolume: 'high',
  ambientVolume: 'high',  // Drone/hum at high volume
  effectsVolume: 'medium',
  ambientEnabled: true,
  ambientTheme: 'spacio', // Default to cosmic drone/hum
  effectsEnabled: true,
});

export const loadSoundPrefs = () => {
  try {
    const stored = localStorage.getItem(SOUND_PREFS_KEY);
    return stored ? { ...getDefaultSoundPrefs(), ...JSON.parse(stored) } : getDefaultSoundPrefs();
  } catch {
    return getDefaultSoundPrefs();
  }
};

export const saveSoundPrefs = (prefs) => {
  try {
    localStorage.setItem(SOUND_PREFS_KEY, JSON.stringify(prefs));
  } catch (e) {
    console.warn('Failed to save sound preferences:', e);
  }
};

// Apply saved preferences
export const applySoundPrefs = (prefs) => {
  if (!prefs.enabled) {
    setMasterVolume(0);
    AmbientSounds.stop();
    return;
  }
  
  setMasterVolume(prefs.masterVolume);
  setAmbientVolume(prefs.ambientEnabled ? prefs.ambientVolume : 0);
  setEffectsVolume(prefs.effectsEnabled ? prefs.effectsVolume : 0);
};

export default {
  SoundEffects,
  AmbientSounds,
  resumeAudio,
  setMasterVolume,
  setAmbientVolume,
  setEffectsVolume,
  VOLUME_LEVELS,
  loadSoundPrefs,
  saveSoundPrefs,
  applySoundPrefs,
  getDefaultSoundPrefs,
};
